import { ZodError, ZodIssue } from 'zod';

type ValidationIssue = ZodIssue;

const FIELD_NAMES: Record<string, string> = {
  publisherName: 'Publisher Name',
  website: 'Website',
  country: 'Country',
  address1: 'Street Address',
  address2: 'Address Line 2',
  city: 'City',
  state: 'State/Province',
  zip: 'ZIP/Postal Code',
  name: 'Name',
  email: 'Email',
  fullName: 'Full Name',
  password: 'Password',
  passwordRepeat: 'Confirm Password',
};

const FIELD_GROUPS: Record<string, { name: string; fields: string[] }> = {
  address: {
    name: 'Address',
    fields: ['country', 'address1', 'address2', 'city', 'state', 'zip'],
  },
  publisher: {
    name: 'Publisher Information',
    fields: ['publisherName', 'website'],
  },
  password: {
    name: 'Password',
    fields: ['password', 'passwordRepeat'],
  },
};

function getFieldDisplayName(fieldPath: (string | number)[]): string {
  const fieldName = fieldPath[0]?.toString() || '';
  return FIELD_NAMES[fieldName] || fieldName;
}

function normalizeIssuePath(path: PropertyKey[]): (string | number)[] {
  return path.filter(
    (segment): segment is string | number =>
      typeof segment === 'string' || typeof segment === 'number',
  );
}

function isMissingStringIssue(issue: ValidationIssue): boolean {
  return (
    issue.code === 'invalid_type' &&
    'expected' in issue &&
    issue.expected === 'string' &&
    issue.message.includes('received undefined')
  );
}

function getAddressErrorMessage(issues: ValidationIssue[]): string {
  const addressFields = [
    'country',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
  ];
  const missingFields = issues
    .filter(
      (issue) =>
        isMissingStringIssue(issue) &&
        addressFields.includes(issue.path[0]?.toString() || ''),
    )
    .map((issue) => getFieldDisplayName(normalizeIssuePath(issue.path)));

  if (missingFields.length === 0) {
    return 'Address information is invalid';
  }

  if (missingFields.length === 1) {
    return `${missingFields[0]} is required`;
  }

  if (missingFields.length === addressFields.length) {
    return 'Complete address information is required';
  }

  const lastField = missingFields.pop();
  const otherFields = missingFields.join(', ');
  return `${otherFields} and ${lastField} are required`;
}

function getPublisherErrorMessage(issues: ValidationIssue[]): string {
  const publisherFields = ['publisherName', 'website'];
  const missingFields = issues
    .filter(
      (issue) =>
        isMissingStringIssue(issue) &&
        publisherFields.includes(issue.path[0]?.toString() || ''),
    )
    .map((issue) => getFieldDisplayName(normalizeIssuePath(issue.path)));

  if (missingFields.length === 0) {
    return 'Publisher information is invalid';
  }

  if (missingFields.length === 1) {
    return `${missingFields[0]} is required`;
  }

  const lastField = missingFields.pop();
  const otherFields = missingFields.join(', ');
  return `${otherFields} and ${lastField} are required`;
}

function categorizeIssues(issues: ValidationIssue[]): {
  address: ValidationIssue[];
  publisher: ValidationIssue[];
  password: ValidationIssue[];
  other: ValidationIssue[];
} {
  const categories = {
    address: [] as ValidationIssue[],
    publisher: [] as ValidationIssue[],
    password: [] as ValidationIssue[],
    other: [] as ValidationIssue[],
  };

  issues.forEach((issue) => {
    const fieldName = issue.path[0]?.toString() || '';

    if (FIELD_GROUPS.address.fields.includes(fieldName)) {
      categories.address.push(issue);
    } else if (FIELD_GROUPS.publisher.fields.includes(fieldName)) {
      categories.publisher.push(issue);
    } else if (FIELD_GROUPS.password.fields.includes(fieldName)) {
      categories.password.push(issue);
    } else {
      categories.other.push(issue);
    }
  });

  return categories;
}

function getSingleFieldErrorMessage(issue: ValidationIssue): string {
  const fieldName = getFieldDisplayName(normalizeIssuePath(issue.path));

  switch (issue.code) {
    case 'invalid_type':
      if (isMissingStringIssue(issue)) {
        return `${fieldName} is required`;
      }
      return `${fieldName} has an invalid format`;

    case 'too_small':
      return `${fieldName} is too short`;

    case 'too_big':
      return `${fieldName} is too long`;

    case 'invalid_format':
      if ('format' in issue && issue.format === 'email') {
        return `${fieldName} must be a valid email address`;
      }
      if ('format' in issue && issue.format === 'url') {
        return `${fieldName} must be a valid URL`;
      }
      return `${fieldName} has an invalid format`;

    default:
      return issue.message || `${fieldName} is invalid`;
  }
}

function formatIssues(issues: ValidationIssue[]): string {
  const categories = categorizeIssues(issues);
  const messages: string[] = [];

  if (categories.address.length > 0) {
    messages.push(getAddressErrorMessage(categories.address));
  }

  if (categories.publisher.length > 0) {
    messages.push(getPublisherErrorMessage(categories.publisher));
  }

  if (categories.password.length > 0) {
    categories.password.forEach((issue) => {
      messages.push(getSingleFieldErrorMessage(issue));
    });
  }

  if (categories.other.length > 0) {
    categories.other.forEach((issue) => {
      messages.push(getSingleFieldErrorMessage(issue));
    });
  }

  return messages.length > 0
    ? messages.join('. ')
    : 'Please check your form and try again';
}

export function parseValidationErrors(error: unknown): string {
  if (Array.isArray(error)) {
    try {
      return formatIssues(error as ValidationIssue[]);
    } catch {
      // Fall through to other error handling
    }
  }

  if (error instanceof ZodError) {
    return formatIssues(error.issues);
  }

  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      return parseValidationErrors(parsed);
    } catch {
      if (
        error.includes('validation') ||
        error.includes('Required') ||
        error.includes('invalid_type')
      ) {
        return 'Please fill in all required fields correctly';
      }
      return error;
    }
  }

  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof error.message === 'string') {
      try {
        const parsed = JSON.parse(error.message);
        return parseValidationErrors(parsed);
      } catch {
        return error.message;
      }
    }

    if ('issues' in error && Array.isArray(error.issues)) {
      return parseValidationErrors(error.issues);
    }
  }

  return 'An error occurred. Please check your form and try again';
}
